import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BarChart3, Briefcase, CreditCard, Eye, Home, Users } from "lucide-react";
import { Toaster } from "sonner";
import { NOMBRE_INSTALACION, validarEntorno } from "@/reclutamiento/modo";
import type { SesionPortal } from "@/reclutamiento/tipos";
import { crearPortalApi, type PortalApi } from "./api";
import { Ctx, type PortalCtx } from "./contexto";
import { Logo } from "./iconos";
import { navegar, useRuta, type Pantalla } from "./ruta";
import { borrarCache, mensajeError, useAltoComoVariable } from "./util";
import { alReconectar, esErrorDeRed } from "./red";
import { conPendientes, detenerPendientes, iniciarPendientes } from "./pendientes";
import { esErrorDeChunk, recargarPorChunk } from "./recarga";
import { AvisoConexion } from "./AvisoConexion";
import { BotonAyuda, BotonTema, GuiaPrimeraVez, HojaAyuda } from "./claridad";
import { useTema } from "./tema";
import { guiaVista, marcarGuiaVista } from "./claridad-textos";
import { Entrada, CrearContrasena } from "./pantallas/Entrada";
import { Inicio } from "./pantallas/Inicio";
import { Candidatos } from "./pantallas/Candidatos";
import { Ficha } from "./pantallas/Ficha";
import { Vacantes } from "./pantallas/Vacantes";
import { Cuenta } from "./pantallas/Cuenta";
import { Metricas } from "./pantallas/Metricas";

type Estado =
  | { tipo: "cargando" }
  | { tipo: "error"; msg: string; red?: boolean }
  | { tipo: "entrada" }
  | { tipo: "nueva" }
  | { tipo: "listo"; sesion: SesionPortal };

// La visita se registra una sola vez por carga de la página (StrictMode monta dos veces).
let visitaRegistrada = false;

async function resolverSesion(api: PortalApi): Promise<Estado> {
  const s = await api.sesion();
  if (!s) return { tipo: "entrada" };
  if (!visitaRegistrada && !s.solo_lectura && !api.verComo) {
    visitaRegistrada = true;
    try {
      await api.registrarVisita();
    } catch {
      /* no bloquea la entrada; solo afecta el conteo de «nuevos» */
    }
    return { tipo: "listo", sesion: (await api.sesion()) ?? s };
  }
  return { tipo: "listo", sesion: s };
}

let arranque: Promise<{ api: PortalApi | null; estado: Estado }> | null = null;
function arrancar() {
  arranque ??= (async () => {
    const falta = validarEntorno();
    if (falta) return { api: null, estado: { tipo: "error", msg: falta } as Estado };
    try {
      const api = await crearPortalApi();
      if (await api.pideContrasenaNueva()) return { api, estado: { tipo: "nueva" } as Estado };
      return { api, estado: await resolverSesion(api) };
    } catch (e) {
      // Pestaña vieja tras publicar: recarga una vez. Falla de red: se vuelve a intentar (no se queda el error).
      if (esErrorDeChunk(e)) recargarPorChunk();
      const red = esErrorDeRed(e) || esErrorDeChunk(e);
      if (red) arranque = null;
      return { api: null, estado: { tipo: "error", msg: mensajeError(e, "No pudimos abrir el portal."), red } as Estado };
    }
  })();
  return arranque;
}

export function App() {
  const [api, setApi] = useState<PortalApi | null>(null);
  const [estado, setEstado] = useState<Estado>({ tipo: "cargando" });

  useEffect(() => {
    let vivo = true;
    arrancar().then((r) => {
      if (!vivo) return;
      setApi(r.api);
      setEstado(r.estado);
    });
    return () => {
      vivo = false;
    };
  }, []);

  // Si no se pudo abrir por la red, se sigue intentando solo (al regresar la red, al volver el foco, cada ~20 s).
  const errorDeRed = estado.tipo === "error" && !!estado.red;
  useEffect(() => {
    if (!errorDeRed) return;
    let vivo = true;
    const apagar = alReconectar(() => {
      arrancar().then((r) => {
        if (!vivo || (r.estado.tipo === "error" && r.estado.red)) return;
        setApi(r.api);
        setEstado(r.estado);
      });
    });
    return () => {
      vivo = false;
      apagar();
    };
  }, [errorDeRed]);

  const tras = useCallback(async () => {
    if (!api) return;
    try {
      setEstado(await resolverSesion(api));
    } catch (e) {
      const red = esErrorDeRed(e);
      if (red) arranque = null;
      setEstado({ tipo: "error", msg: mensajeError(e, "No pudimos abrir el portal."), red });
    }
  }, [api]);

  const salir = useCallback(async () => {
    if (!api) return;
    try {
      await api.salir();
    } finally {
      borrarCache();
      detenerPendientes();
      visitaRegistrada = false;
      navegar("#/inicio", true);
      setEstado({ tipo: "entrada" });
    }
  }, [api]);

  if (estado.tipo === "cargando")
    return (
      <div className="pt entrada" aria-busy="true">
        <div className="entrada-in">
          <div className="sk" style={{ height: 32, width: 180 }} />
          <div className="sk" style={{ height: 160 }} />
        </div>
      </div>
    );
  if (estado.tipo === "error" || !api)
    return (
      <div className="pt entrada">
        <div className="entrada-in">
          <div className="logo">
            <Logo /> Portal de clientes
          </div>
          <div className={"errbox" + (errorDeRed ? " suave" : "")} role={errorDeRed ? "status" : "alert"}>
            <b>{estado.tipo === "error" ? estado.msg : "No pudimos abrir el portal."}</b>
            {errorDeRed ? (
              <button
                className="btn"
                onClick={() =>
                  void arrancar().then((r) => {
                    setApi(r.api);
                    setEstado(r.estado);
                  })
                }
              >
                Reintentar ahora
              </button>
            ) : (
              <button className="btn" onClick={() => window.location.reload()}>
                Recargar
              </button>
            )}
          </div>
        </div>
      </div>
    );
  if (estado.tipo === "nueva") return <CrearContrasena api={api} onListo={tras} />;
  if (estado.tipo === "entrada") return <Entrada api={api} onListo={tras} />;
  return <Shell api={api} sesion={estado.sesion} salir={salir} />;
}

const TABS: [Pantalla, string, typeof Home][] = [
  ["inicio", "Inicio", Home],
  ["candidatos", "Candidatos", Users],
  ["metricas", "Métricas", BarChart3],
  ["vacantes", "Vacantes", Briefcase],
  ["cuenta", "Cuenta", CreditCard],
];

function Shell({ api: apiBase, sesion, salir }: { api: PortalApi; sesion: SesionPortal; salir: () => void }) {
  const r = useRuta();
  // Lo que no se pudo guardar por falta de red se queda en cola y se manda solo; las lecturas ya lo traen puesto.
  const api = useMemo(() => conPendientes(apiBase), [apiBase]);
  const escribe = !sesion.solo_lectura && !apiBase.verComo;
  const dueno = `${sesion.cliente.id}|${sesion.usuario?.correo ?? "-"}`;
  useEffect(() => (escribe ? iniciarPendientes(api, dueno) : undefined), [api, dueno, escribe]);
  const cabeza = useRef<HTMLElement>(null);
  useAltoComoVariable(cabeza, "--ptop-h");
  const soloLectura = sesion.solo_lectura || !!api.verComo;
  const ctx = useMemo<PortalCtx>(
    () => ({ api, sesion, soloLectura, bloqueado: sesion.bloqueado, salir }),
    [api, sesion, soloLectura, salir],
  );
  const activa: Pantalla = r.pantalla === "candidato" ? "candidatos" : r.pantalla;
  // Ayuda siempre a la mano y, la primera vez después de esta versión, la guía corta (se cierra y no vuelve).
  const [ayuda, setAyuda] = useState(false);
  const [tema, setTema] = useTema();
  const [guia, setGuia] = useState(() => !guiaVista());
  const cerrarGuia = useCallback(() => {
    marcarGuiaVista();
    setGuia(false);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [r.pantalla, r.id]);
  useEffect(() => {
    const t = TABS.find((x) => x[0] === activa)?.[1] ?? "Portal";
    document.title = `${t} · ${sesion.cliente.nombre}`;
  }, [activa, sesion.cliente.nombre]);

  const pantalla =
    r.pantalla === "candidato" && r.id ? (
      <Ficha key={r.id} id={r.id} />
    ) : r.pantalla === "candidatos" ? (
      <Candidatos />
    ) : r.pantalla === "metricas" ? (
      <Metricas />
    ) : r.pantalla === "vacantes" ? (
      <Vacantes />
    ) : r.pantalla === "cuenta" ? (
      <Cuenta />
    ) : (
      <Inicio />
    );

  const badge = (id: Pantalla) => (id === "cuenta" && sesion.bloqueado ? <i aria-label="Pago pendiente">!</i> : null);

  return (
    <Ctx.Provider value={ctx}>
      <div className="pt pt-app">
        <header className="ptop" ref={cabeza}>
          {soloLectura && (
            <div className="vercomo" role="status">
              <Eye size={16} aria-hidden="true" />
              <span>
                Estás viendo lo que ve {sesion.cliente.nombre} · solo lectura
              </span>
            </div>
          )}
          <div className="ptop-in">
            <div className="pbrand">
              <Logo s={30} />
              <div>
                <span className="cn">{sesion.cliente.nombre}</span>
                <span className="inst">{NOMBRE_INSTALACION}</span>
              </div>
            </div>
            <nav className="dnav" aria-label="Secciones del portal">
              {TABS.map(([id, l, I]) => (
                <a key={id} href={`#/${id}`} aria-current={activa === id ? "page" : undefined}>
                  <span className="tb">
                    <I size={17} aria-hidden="true" />
                    {badge(id)}
                  </span>
                  {l}
                </a>
              ))}
            </nav>
            {sesion.usuario && <span className="pusr">{sesion.usuario.correo}</span>}
            <BotonTema tema={tema} onTema={setTema} />
            <BotonAyuda onAbrir={() => setAyuda(true)} />
          </div>
          <AvisoConexion />
        </header>
        <main className={"pmain" + (r.pantalla === "cuenta" ? " angosto" : "")}>
          {guia && r.pantalla !== "candidato" && <GuiaPrimeraVez onCerrar={cerrarGuia} />}
          {pantalla}
        </main>
        {ayuda && <HojaAyuda onCerrar={() => setAyuda(false)} onGuia={() => setGuia(true)} />}
        <nav className="ptabs" aria-label="Secciones del portal">
          {TABS.map(([id, l, I]) => (
            <a key={id} href={`#/${id}`} aria-current={activa === id ? "page" : undefined}>
              <span className="tb">
                <I size={22} aria-hidden="true" />
                {badge(id)}
              </span>
              {l}
            </a>
          ))}
        </nav>
        <Toaster
          position="bottom-center"
          theme={tema === "oscuro" ? "dark" : "light"}
          offset={24}
          mobileOffset={{ bottom: "calc(84px + env(safe-area-inset-bottom))" }}
          toastOptions={{ style: { fontFamily: "var(--f-body)", fontSize: 14 } }}
        />
      </div>
    </Ctx.Provider>
  );
}
